import { PlusOutlined } from "@ant-design/icons";
import {
  BUTTON_HEIGHT,
  DATE_TIME_FORMAT,
  FONT_SIZE,
  FONT_SIZE_VALUE,
  MENU_COLORS,
} from "@lhd/constants";
import { generateUuid } from "@lhd/features/Apps/utils";
import {
  useDeleteConfigTax,
  useFetchConfigsTax,
  useSubmitConfigTax,
} from "@lhd/hooks";
import { DISPLAY_SIZES, IndustryCategory, ItemInsuranceData } from "@lhd/types";
import { Form, notification, Popconfirm, Tooltip } from "antd";
import dayjs from "dayjs";
import { cloneDeep, get, isString } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import InsuranceTax from "./InsuranceTax";
import StartDate from "./StartDate";
import Button from "@lhd/components/Button";
import { useSelector } from "react-redux";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import ButtonCommon from "@lhd/components/ButtonCommon";

type DataType = {
  children: ItemInsuranceData[];
} & IndustryCategory;
const useTableConfigTax = () => {
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const refOriginalValues = useRef(null);
  const [rowEditing, setRowEditing] = useState<{
    [key: number]: number | string | null; // key id of item of industry_category // value is id of item of industry_category_tax_config
  }>({});

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [form] = Form.useForm();

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];

  const {
    data: industryCategoriesData,
    isLoading: isLoadingIndustryCategories,
  } = useFetchConfigsTax();

  const checkPastDate = (dateStr: string) => {
    if (!dateStr) return false;
    let inputDate = dayjs(dateStr, DATE_TIME_FORMAT.DATE_JP, true);
    if (!inputDate.isValid()) {
      inputDate = dayjs(dateStr);
    }
    if (!inputDate.isValid()) return false;
    const today = dayjs().startOf("day");
    return !inputDate.isAfter(today, "day");
  };

  const checkEditing = (record: any): boolean => {
    return (
      !!rowEditing && rowEditing[record.industry_category_id] === record.id
    );
  };

  const { mutate, isLoading: isSubmitting } = useSubmitConfigTax();
  const { mutate: mutateDelete, isLoading: isDeleting } = useDeleteConfigTax();

  const dataSourceInit = useMemo(() => {
    if (!industryCategoriesData) return [];
    return industryCategoriesData.data.map((industry, index) => {
      let nextStartDate: string | null = null;
      const dataPreItem = dataSource.find(
        (x) =>
          x.id === industry.id &&
          x.id !== (industryCategoriesData as any).industryCategoryIdPreUpdate,
      );

      const children = industry.industry_category_tax_configs
        .sort(
          (a, b) =>
            dayjs(b.start_date).valueOf() - dayjs(a.start_date).valueOf(),
        )
        .map((item, index, originalArray) => {
          const nextItem = originalArray[index + 1];
          const prevStartDate = nextItem?.start_date;
          const newItem = {
            ...item,
            nextStartDate,
            prevStartDate,
            key: item.id + "",
          };
          nextStartDate = item.start_date;
          return newItem;
        });
      if (
        dataPreItem &&
        dataPreItem.children[0] &&
        isString(dataPreItem.children[0].id)
      ) {
        children.unshift(dataPreItem.children[0] as any);
      }
      return {
        key: "rowAsHeader_" + industry.id,
        action: "header",
        ...industry,
        children,
      };
    });
  }, [industryCategoriesData]);

  useEffect(() => {
    const result: any = {};

    industryCategoriesData?.data.forEach((industryCategory) => {
      industryCategory.industry_category_tax_configs.forEach(
        (industryCategoryTaxConfig) => {
          const industry_category_id =
            industryCategoryTaxConfig.industry_category_id.toString();

          const id = industryCategoryTaxConfig.id.toString();

          if (!result[industry_category_id]) {
            result[industry_category_id] = {
              [id]: {},
            };
          }

          if (!result[industry_category_id][id]) {
            result[industry_category_id][id] = {};
          }

          result[industry_category_id][id]["insurance_tax"] =
            industryCategoryTaxConfig.insurance_tax;

          result[industry_category_id][id]["start_date"] = dayjs(
            industryCategoryTaxConfig.start_date,
          );
          result[industry_category_id][id]["end_date"] =
            industryCategoryTaxConfig.end_date
              ? dayjs(industryCategoryTaxConfig.end_date)
              : null;
        },
      );
    });
    refOriginalValues.current = result;
    form.setFieldsValue(result);
  }, [industryCategoriesData]);

  useEffect(() => {
    // when we switch between edit and non edit . reset value
    form.setFieldsValue(refOriginalValues.current || {});
  }, [rowEditing]);

  useEffect(() => {
    setDataSource(dataSourceInit);
  }, [dataSourceInit]);

  const addRow = (
    recordKey: string,
    isCollapse: boolean,
    idIndustry: number,
  ) => {
    if (isCollapse) setExpandedKeys([...expandedKeys, recordKey]);
    const dataSourceClone = cloneDeep(dataSource);
    const item = dataSourceClone.find((x) => x.id == idIndustry);
    const id = generateUuid();

    if (item) {
      const firstItem = item.children && item.children[0];
      item.children = [
        {
          id: id,
          industry_category_id: idIndustry,
          insurance_tax: null,
          start_date: null,
          isNew: true,
          end_date: null,
          prevStartDate: firstItem?.start_date,
        },
        ...item.children,
      ];
    }
    setDataSource(dataSourceClone);
    setRowEditing({
      ...rowEditing,
      [idIndustry]: id,
    });
  };

  const removeRow = (id: string | number, idIndustry: number) => {
    try {
      const isNew = isString(id);
      if (isNew) {
        const dataSourceClone = cloneDeep(dataSource);
        const item = dataSourceClone.find((x) => x.id == idIndustry);
        if (item) {
          const children = item.children?.filter((child) => child.id != id);
          item.children = children;
          item.industry_category_tax_configs = children;
        }
        setDataSource(dataSourceClone);
        setRowEditing({ ...rowEditing, [idIndustry]: null });
        return;
      }
      const payload = {
        delete: id,
      };
      mutateDelete({
        payload,
        industryCategoryId: idIndustry,
        industryCategoryTaxConfigId: id,
        cb: () => {
          setRowEditing({ ...rowEditing, [idIndustry]: null });
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  const onSubmit = async (
    industryCategoryId: number | string,
    id: number | string,
  ) => {
    try {
      const dataValidated = await form.validateFields([
        [industryCategoryId.toString(), id.toString(), "start_date"],
        [industryCategoryId.toString(), id.toString(), "insurance_tax"],
      ]);
      if (dataValidated.errorFields) return;
      const values = form.getFieldsValue();
      const data = get(values, `[${industryCategoryId}][${id}]`, null);
      if (!data) return;
      if (data.insurance_tax < 0 || data.insurance_tax > 100) {
        return notification["error"]({
          message: "%の値は0～100の半角数字を入力してください。",
        });
      }
      const payload = {
        insurance_tax: data.insurance_tax,
        start_date: data.start_date.format(DATE_TIME_FORMAT.DATE_QUERY),
      };

      const isNew = isString(id);
      const industryCategoryTaxConfigId = isNew ? null : id;
      await mutate({
        payload,
        industryCategoryId,
        industryCategoryTaxConfigId,
        cb: () => {
          setRowEditing({ ...rowEditing, [industryCategoryId]: null });
        },
      });
    } catch (e: any) {
      if (e.errorFields) return;
      setRowEditing({ ...rowEditing, [industryCategoryId]: null });
    }
  };

  function handleTurnOnEditing(
    industry_category_id: any,
    id: any,
    isEdit: boolean,
  ) {
    const dataSourceClone = cloneDeep(dataSource);
    const item = dataSourceClone.find((x) => x.id === industry_category_id);
    if (item?.children) {
      item.children = item.children.filter((x) => !isString(x.id));
      setDataSource(dataSourceClone);
    }

    setRowEditing({
      ...rowEditing,
      [industry_category_id]: isEdit ? null : id,
    });
  }

  const columns = [
    {
      title: "name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "insurance_tax", //率 (%)
      dataIndex: "insurance_tax",
      key: "insurance_tax",
      width: 100,
      render: (_: any, record: any) => {
        const isRowAsHeader = record.action === "header";
        const isEdit = !isRowAsHeader && checkEditing(record);
        const isCollapse = isRowAsHeader && !expandedKeys.includes(record.key);
        return (
          <InsuranceTax
            isRowAsHeader={isRowAsHeader}
            isEdit={isEdit}
            record={record}
            isCollapse={isCollapse}
          />
        );
      },
    },
    {
      width: 370,
      title: "start_date", //有効期間
      dataIndex: "start_date",
      key: "start_date",
      render: (_: any, record: any) => {
        const isRowAsHeader = record.action === "header";
        const isEdit = !isRowAsHeader && checkEditing(record);
        const isCollapse = isRowAsHeader && !expandedKeys.includes(record.key);
        return (
          <StartDate
            isRowAsHeader={isRowAsHeader}
            isEdit={isEdit}
            record={record}
            isCollapse={isCollapse}
          />
        );
      },
    },
    {
      title: "action", //+ 閉じる
      dataIndex: "action",
      key: "action",
      width: 240,
      render: (_: any, record: any) => {
        const isRowAsHeader = record.action === "header";
        const isCollapse = isRowAsHeader && !expandedKeys.includes(record.key);
        const isPastDate = !isRowAsHeader && checkPastDate(record.start_date);
        const isEdit = !isRowAsHeader && checkEditing(record);
        const isNew = isString(record.id);
        const btnAdd = (
          <ButtonCommon
            disabled={!!rowEditing[record.id]}
            onClick={() => addRow(record.key, isCollapse, record.id)}
            icon={
              <div className="flex-center">
                <PlusOutlined />
              </div>
            }
            name="行追加"
            style={{
              fontSize: size,
              height: height - 3,
              width: "fit-content",
              gap: 5,
            }}
          />
        );
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {/* btn right */}
            {isRowAsHeader ? (
              rowEditing[record.id] ? (
                <Tooltip title="率(%）と有効期限が必須項目です。入力してください。">
                  {btnAdd}
                </Tooltip>
              ) : (
                btnAdd
              )
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: 7,
                  flex: 1,
                }}
              >
                {!isNew && (
                  <ButtonCommon
                    name={isEdit ? "キャンセル" : "編集"}
                    type={isEdit ? "cancel-btn" : "submit-btn"}
                    disabled={isPastDate}
                    onClick={() => {
                      handleTurnOnEditing(
                        record.industry_category_id,
                        record.id,
                        isEdit,
                      );
                    }}
                    style={{
                      fontSize: size,
                      height: height - 3,
                      width: "fit-content",
                    }}
                  />
                )}
                <ButtonCommon
                  name="保存"
                  onClick={() => {
                    onSubmit(record.industry_category_id, record.id);
                  }}
                  disabled={isPastDate || !isEdit}
                  style={{
                    fontSize: size,
                    height: height - 3,
                    width: "fit-content",
                    gap: 5,
                  }}
                />

                <Popconfirm
                  title="削除すると、このレコードは復元できなくなりますが、よろしいですか?"
                  onConfirm={() =>
                    removeRow(record.id, record.industry_category_id)
                  }
                  placement="bottomRight"
                  autoAdjustOverflow={false}
                  okText="はい"
                  cancelText="いいえ"
                  overlayStyle={{
                    width: 520,
                  }}
                  disabled={isPastDate}
                >
                  <ButtonCommon
                    name="削除"
                    type="delete-btn"
                    disabled={isPastDate}
                    style={{
                      fontSize: size,
                      height: height - 3,
                      width: "fit-content",
                    }}
                  />
                </Popconfirm>
              </div>
            )}

            {/* btn collapse of expanse */}
            {isRowAsHeader && (
              <ButtonCommon
                onClick={() => {
                  if (!isRowAsHeader) return;

                  if (isCollapse) {
                    setExpandedKeys([...expandedKeys, record.key]);
                  } else {
                    const newExpandedKeys = expandedKeys.filter(
                      (key) => key !== record.key,
                    );
                    setExpandedKeys(newExpandedKeys);
                  }
                }}
                name={isCollapse ? "表示" : "非表示"}
                style={{
                  fontSize: size,
                  height: height - 3,
                  width: "fit-content",
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  return {
    dataSource,
    columns,
    expandedKeys,
    setExpandedKeys,
    isLoading: isSubmitting || isLoadingIndustryCategories || isDeleting,
    form,
    displaySize,
  };
};

export type Props = ReturnType<typeof useTableConfigTax>;

export default useTableConfigTax;
